/**
 * EXAMPLE ONLY!
 * Helper utility functions
 */

/**
 * Debounces a function call
 */
export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null;
    
    return function executedFunction(...args: Parameters<T>) {
        const later = () => {
            timeout = null;
            func(...args);
        };
        
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(later, wait);
    };
}

/**
 * Formats a number as currency
 */
export function formatCurrency(amount: number, currency: string = 'EUR', locale: string = 'de-DE'): string {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
    }).format(amount);
}

/**
 * Formats a date
 */
export function formatDate(date: Date | string, locale: string = 'de-DE'): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale).format(dateObj);
}

/**
 * Waits for a specified amount of time
 */
export function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Escapes HTML to prevent XSS
 */
export function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Escapes a string for safe insertion into HTML attribute or text contexts.
 * Unlike {@link escapeHtml} this variant also escapes single quotes,
 * making it suitable for use inside both single- and double-quoted HTML attributes.
 *
 * @param value - The raw string to escape. Null or undefined values are treated as empty strings.
 * @returns The HTML-escaped string.
 */
export function escapeHtmlAttr(value: string): string {
    return (value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

